import React from "react";
import { Card, Typography, Row, Col, Button, Tag, Badge } from "antd";
import {
  CalculatorOutlined,
  ContainerOutlined,
  TeamOutlined,
  CheckSquareOutlined,
  UserOutlined,
  GlobalOutlined,
  LineChartOutlined,
  CloudDownloadOutlined,
  MailOutlined,
  NotificationOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import { usePendingCompaniesCount } from "../hooks/usePendingCompaniesCount";
import { usePendingTasksCountForUser } from "../hooks/usePendingTasksCountForUser";

const { Title } = Typography;

const Container = styled.div`
  padding: 16px;
`;

const AdminCard = styled(Card)`
  height: 100%;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-3px);
  }

  .ant-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const IconWrapper = styled.div`
  font-size: 36px;
  margin-bottom: 10px;
  color: #1890ff;
`;

const CardTitle = styled(Title)`
  margin-top: 0 !important;
  margin-bottom: 4px !important;
  text-align: center;
`;

const WipTag = styled(Tag)`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 11px;
`;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { count: pendingCompaniesCount } = usePendingCompaniesCount();
  const { count: pendingTasksCountForUser } = usePendingTasksCountForUser();

  const handleNavigate = (path: string, feature: string) => {
    navigate(path);
    trackFeatureUsage(`admin_${feature}`);
  };

  const adminSections = [
    { title: "Bookings", icon: <ContainerOutlined />, path: "/admin/bookings", feature: "bookings" },
    { title: "Containers", icon: <ContainerOutlined />, path: "/admin/containers", feature: "containers" },
    { title: "Tariffs", icon: <ContainerOutlined />, path: "/admin/tariffs", feature: "tariffs" },
    { title: "Companies", icon: <TeamOutlined />, path: "/admin/companies", feature: "companies", badgeCount: pendingCompaniesCount },
    { title: "Tasks", icon: <CheckSquareOutlined />, path: "/admin/tasks", feature: "tasks", badgeCount: pendingTasksCountForUser },
    { title: "Users", icon: <UserOutlined />, path: "/admin/users", feature: "users" },
    { title: "Ports", icon: <GlobalOutlined />, path: "/admin/ports", feature: "ports" },
    { title: "Invoice Settings", icon: <CalculatorOutlined />, path: "/admin/pricing", feature: "pricing" },
    { title: "Invoices", icon: <CalculatorOutlined />, path: "/admin/invoices", feature: "invoices" },
    { title: "Reports", icon: <LineChartOutlined />, path: "/admin/reports", feature: "reports", wip: true },
    { title: "Backups", icon: <CloudDownloadOutlined />, path: "/admin/backups", feature: "backups" },
    { title: "Email Notifications", icon: <MailOutlined />, path: "/admin/email-notifications", feature: "email-notifications" },
    { title: "Announcement banner", icon: <NotificationOutlined />, path: "/admin/announcement-banner", feature: "announcement-banner" },
    { title: "Blog", icon: <FileTextOutlined />, path: "/admin/blog", feature: "blog" },
    { title: "Automations", icon: <ThunderboltOutlined />, path: "/admin/automations", feature: "automations" },
    { title: "Bot", icon: <RobotOutlined />, path: "/admin/bot", feature: "bot" },
  ];

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>Admin Dashboard</Title>

      <Row gutter={[12, 12]}>
        {adminSections.map((section) => (
          <Col xs={24} sm={12} md={8} lg={6} key={section.path}>
            <AdminCard
              onClick={() => handleNavigate(section.path, section.feature)}
            >
              {section.wip && <WipTag color="warning">Work in progress</WipTag>}
              {section.badgeCount !== undefined && section.badgeCount > 0 && (
                <Badge
                  count={section.badgeCount}
                  offset={[10, -10]}
                  style={{ position: "absolute", top: 8, right: 8 }}
                >
                  <div />
                </Badge>
              )}
              <IconWrapper>{section.icon}</IconWrapper>
              <CardTitle level={5}>{section.title}</CardTitle>
              <Button type="primary" size="small">
                Go to {section.title}
              </Button>
            </AdminCard>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default AdminDashboard;
